import { IResourceComponentsProps, useExport, useGetIdentity, useList, useNavigation } from '@pankod/refine-core'

import {
  List,
  RefreshButton,
  CreateButton,
  ExportButton,
  Table,
  useTable,
  Space,
  EditButton,
  DeleteButton,
  DateField,
  getDefaultSortOrder,
  useModalForm,
  Icons,
  useModal,
  Button,
  ImportButton,
  useImport,
  notification,
  Input,
  Divider,
  Form,
  SaveButton,
  Row,
  message,
  Badge,
} from '@pankod/refine-antd'
import type { NotificationPlacement } from 'antd/es/notification'
import { UploadTopics } from './import_topics'
import { ITopic, ISubTheme, IReportTheme } from 'interfaces'
import { CreateTopic } from './create'
import { EditTopic } from './edit'
import { useUserContext } from 'context/user'
import Permissions from 'permissions'
import { useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'
import { createUserHistory } from 'gql/mutations'
import { topicImportFormat } from 'helpers/import_template_format/importTemplate'
import AllQuestionFilter from 'components/filters/allQuestionFilter'

type Permission = {
  granted?: boolean
}
const { PlusOutlined, ExportOutlined, CopyOutlined } = Icons

export const TopicsList: React.FC<IResourceComponentsProps> = () => {
  const { data: user } = useGetIdentity()
  const { userRole } = useUserContext()
  const hiddenRole = ['srmanager', 'subjectincharge', 'qcreator', 'qreviewer', 'qapprover']
  const [pagination, setHasPagination] = useState<any>(true)
  const [currentFilter, setCurrentFilter] = useState<any>()
  const { push } = useNavigation()
  const [readPermission, setReadPermission] = useState<Permission>({})
  const { modalProps: uploadProps, show: showUpload, close: closeUpload } = useModal()

  const reloadUploadedData = () => {
    tableQueryResult?.refetch()
  }

  const { tableProps, sorter, tableQueryResult, setFilters, searchFormProps, filters } = useTable<ITopic>({
    hasPagination: pagination,
    initialSorter: [
      {
        field: 'created_at',
        order: 'desc',
      },
    ],
    permanentFilter: [
      {
        field: 'in_use',
        operator: 'eq',
        value: true,
      },
    ],
    metaData: {
      fields: [
        'id',
        'name',
        'sub_theme_id',
        { sub_theme: ['name', 'id', { theme: ['name', 'id', { subject: ['name', 'id'] }, { report_theme: ['name', 'id'] }] }] },
        'created_at',
        { paper_template_sub_sections: ['id', 'name'] },
        { work_requests: ['id'] },
        { questions: ['id'] },
      ],
    },
    onSearch: (values: any) => {
      return [
        {
          field: 'name',
          operator: 'contains',
          value: `%${values.title}%`,
        },
      ]
    },
  })

  //handle filters
  const handleFilters = (newFilters: any) => {
    if (!newFilters?.subject_id && !newFilters?.theme_id && !newFilters?.sub_theme_id) {
      setCurrentFilter({ field_name: '', field_value: '' })
      setFilters([], 'replace')
    } else {
      const fieldName = newFilters?.sub_theme_id
        ? 'sub_theme_id'
        : newFilters?.theme_id
        ? 'sub_theme.theme_id'
        : newFilters?.subject_id
        ? 'sub_theme.theme.subject_id'
        : ''
      const fieldValue = newFilters?.sub_theme_id || newFilters?.theme_id || newFilters?.subject_id || ''
      if (fieldName && fieldValue) {
        setCurrentFilter({ field_name: fieldName, field_value: fieldValue })
        setFilters(
          [
            {
              field: fieldName,
              operator: 'eq',
              value: fieldValue,
            },
          ],
          'replace'
        )
      } else {
        setCurrentFilter({ field_name: '', field_value: '' })
        setFilters([], 'replace')
      }
    }
  }

  const reportThemeLists = useList<IReportTheme>({
    resource: 'report_themes',
    metaData: {
      fields: ['id', 'name', 'topic_ids', 'theme_ids'],
    },
    config: {
      pagination: { pageSize: 10000 },
    },
  })

  useEffect(() => {
    if (userRole !== undefined) {
      setReadPermission(Permissions.can(userRole).readAny('topics'))
    }
  }, [userRole])
  useEffect(() => {
    if (!readPermission?.granted && readPermission?.granted !== undefined) push('/')
  }, [readPermission, userRole])
  const { formProps, modalProps, show } = useModalForm({
    resource: 'topics',
    action: 'create',
  })
  const {
    formProps: editFormProps,
    modalProps: editModalProps,
    show: editShow,
  } = useModalForm({
    resource: 'topics',
    action: 'edit',
    metaData: {
      fields: ['id', 'name', 'sub_theme_id', { sub_theme: ['name', { theme: ['name', { subject: ['name'] }] }] }, 'created_at'],
    },
  })
  const openNotification = (placement: NotificationPlacement, message: string) => {
    notification.error({
      message: `Notification`,
      description: message,
      placement,
    })
  }
  const hideDeleteButton = (record: ITopic): boolean => {
    let ptss = record?.paper_template_sub_sections
    let wr = record?.work_requests
    let q = record?.questions
    if (ptss !== undefined && wr !== undefined && q !== undefined) {
      const shouldHide = ptss.length > 0 || wr.length > 0 || q.length > 0
      return shouldHide
    }
    return false
  }
  const importProps = useImport<ITopic>({
    mapData: item => {
      return {
        name: item.name,
        sub_theme_id: item?.sub_theme_id,
        description: item?.description,
        created_by: user?.id,
      }
    },
    onFinish: results => {
      if (results?.succeeded) {
        createUserHistory('topics', 'imported', '', user?.id, userRole)
      }
      if (results?.errored) {
        openNotification('bottomRight', results?.errored[0]?.response[0]?.message)
      }
    },
  })

  //exporting topics
  const { triggerExport: exportTopicDetails, isLoading: exportLoading } = useExport<ITopic>({
    metaData: {
      fields: ['id', 'name', 'sub_theme_id', { sub_theme: ['name', { theme: ['name', 'id', { subject: ['name', 'id'] }] }] }, 'created_at'],
    },
    exportOptions: {
      filename: `all_topics ${Date.now()}`,
    },
    resourceName: 'topics',
    filters:
      currentFilter?.field_name && currentFilter?.field_value
        ? [
            {
              field: 'in_use',
              operator: 'eq',
              value: true,
            },
            {
              field: currentFilter?.field_name,
              operator: 'eq',
              value: currentFilter?.field_value,
            },
          ]
        : [
            {
              field: 'in_use',
              operator: 'eq',
              value: true,
            },
          ],
    maxItemCount: 100000,
    mapData: item => {
      if (item) {
        console.log(item, 'Item')
        let rtDetailsString = ''
        //finding rt details which are mapped with the topic
        const reportThemeDetails = reportThemeLists?.data?.data.filter(rt => rt?.topic_ids?.length > 0 && rt.topic_ids.includes(item?.id))
        //if topic is mapped with report theme, then creating details string
        if (reportThemeDetails && reportThemeDetails?.length > 0) {
          rtDetailsString = reportThemeDetails.map(rt => `${rt.id} : ${rt.name}`).join('\n')
        }
        return {
          Topic_Id: item.id ?? 'Topic Id Is Not Found',
          Topic_Name: item.name ?? 'Topic Name Is Not Found',
          Sub_Theme_Id: item.sub_theme_id ? item.sub_theme_id : 'Sub Theme Id Is Not Found',
          Sub_Theme_Name: item.sub_theme ? item.sub_theme.name : 'Sub Theme Name Is Not Found',
          Theme_Id: item.sub_theme && item.sub_theme.theme ? item.sub_theme.theme.id : 'Theme Id Is Not Found',
          Theme_Name: item.sub_theme && item.sub_theme.theme ? item.sub_theme.theme.name : 'Theme Name Is Not Found',
          Report_Theme_Details: rtDetailsString != '' ? rtDetailsString : 'Not Found',
          Subject_Id:
            item.sub_theme && item.sub_theme.theme && item.sub_theme.theme.subject ? item.sub_theme.theme.subject.id : 'Subject Id Is Not Found',
          Subject_Name:
            item.sub_theme && item.sub_theme.theme && item.sub_theme.theme.subject ? item.sub_theme.theme.subject.name : 'Subject Name Is Not Found',
        }
      }
    },
    onError: () => {
      return message.error('Topic Export Failed, Please contact AMS Admin')
    },
  })

  const csvData = [topicImportFormat.map(item => item.toString())]
  //finding mapped Report Theme Name
  const renderItem = (themeId: string) => {
    if (!themeId) return null
    const referenceList = reportThemeLists.data?.data || []
    const matchingThemes = referenceList.filter(theme => Array.isArray(theme.theme_ids) && theme.theme_ids.includes(themeId))
    return (
      <ul style={{ paddingLeft: 20, margin: 0 }}>
        {matchingThemes.map((theme, index) => (
          <li key={index}>{theme.name}</li>
        ))}
      </ul>
    )
  }

  const handleCopyRow = (record: any, type: string) => {
    let copyText = ''
    if (type === 'row') {
      const themeId = record.sub_theme?.theme?.id || ''
      const referenceList = reportThemeLists.data?.data || []
      const matchingThemes = referenceList.filter(theme => Array.isArray(theme.theme_ids) && theme.theme_ids.includes(themeId))
      const reportThemesStr = matchingThemes.map((theme, index) => `${index + 1}) ${theme.id} -(${theme.name})`).join(', ')
      const dataToCopy = {
        Topic_Id: record.id ?? 'Not Found',
        Topic_Name: record.name ?? 'Not Found',
        Sub_Theme_Id: record.sub_theme_id ?? 'Not Found',
        Sub_Theme_Name: record.sub_theme?.name ?? 'Not Found',
        Theme_Name: record.sub_theme?.theme?.name ?? 'Not Found',
        Theme_Id: record?.sub_theme?.theme?.id || 'Not Found',
        Subject_Name: record?.sub_theme?.theme?.subject?.name ?? 'Not Found',
        Subject_Id: record?.sub_theme?.theme?.subject?.id || 'Not Found',
        Report_Themes: reportThemesStr || 'None',
      }
      copyText = JSON.stringify(dataToCopy, null, 2)
    } else {
      copyText = record
    }

    navigator.clipboard
      .writeText(copyText)
      .then(() => {
        message.success(type === 'row' ? 'Row data copied to clipboard!' : 'copied!')
      })
      .catch(() => {
        message.error(type === 'row' ? 'Failed to copy row data.' : 'Failed to copy the value')
      })
  }

  return (
    <>
      <List
        title={
          <span>
            Topics{' '}
            <Badge count={tableQueryResult?.data?.total || 0} style={{ backgroundColor: '#6659FF', marginLeft: '8px' }} overflowCount={9999999} />
          </span>
        }
        pageHeaderProps={{
          extra: [
            <Row gutter={[24, 24]}>
              <>
                <Button style={{ marginRight: '10px' }} icon={<Icons.ImportOutlined />} onClick={showUpload} hidden={hiddenRole?.includes(userRole)}>
                  Import Form
                </Button>
                {!hiddenRole?.includes(userRole) ? <ImportButton {...importProps} /> : null}
              </>
              {tableQueryResult && tableQueryResult?.data && tableQueryResult?.data?.total > 0 && (
                <ExportButton style={{ marginRight: '10px', marginLeft: '10px' }} onClick={exportTopicDetails} loading={exportLoading} />
              )}

              <RefreshButton style={{ marginRight: '10px' }} onClick={() => tableQueryResult?.refetch()} />
              {!(userRole === 'qcreator' || userRole === 'qreviewer' || userRole === 'qapprover') && (
                <CreateButton style={{ marginRight: '10px' }} type="primary" icon={<PlusOutlined />} onClick={() => show()} />
              )}
            </Row>,
          ],
        }}
      >
        <Form {...searchFormProps}>
          <Space>
            <Form.Item name="title">
              <Input
                placeholder="Search by title"
                allowClear
                onChange={e => {
                  if (e.type === 'click') {
                    searchFormProps.form?.submit()
                  }
                }}
              />
            </Form.Item>
            <Button icon={<Icons.SearchOutlined />} onClick={searchFormProps.form?.submit}>
              Search
            </Button>
          </Space>

          {!(userRole === 'qcreator' || userRole === 'qreviewer' || userRole === 'qapprover') && (
            <Button key="export paper" type="default" style={{ marginLeft: '5px' }}>
              <CSVLink key="export" data={csvData} filename={`topic_ImportFormat${Math.round(new Date().getTime() / 1000)}`}>
                <ExportOutlined /> Download Import Format
              </CSVLink>
            </Button>
          )}
        </Form>
        <Divider />
        <>
          <AllQuestionFilter
            handleFilters={handleFilters}
            userRole={userRole}
            userId={user?.id}
            searchValue={''}
            hiddenMenu={['difficulty_level', 'blooms', 'request type', 'status', 'topic']}
          />
        </>
        <Table {...tableProps} rowKey="id" size="small">
          <Table.Column
            dataIndex="name"
            title="Name"
            render={(text, record: any) => (
              <span style={{ cursor: 'pointer' }} onClick={() => handleCopyRow(record?.id || '', 'cell')}>
                {text}
              </span>
            )}
          />

          <Table.Column<ITopic>
            dataIndex="sub_theme_id"
            title="Sub Theme"
            render={(text, record: any) => (
              <span style={{ cursor: 'pointer' }} onClick={() => handleCopyRow(record?.sub_theme?.id || '', 'cell')}>
                {record?.sub_theme?.name}
              </span>
            )}
          />
          <Table.Column<ITopic>
            dataIndex="theme"
            title="Theme"
            render={(text, record: any) => (
              <span style={{ cursor: 'pointer' }} onClick={() => handleCopyRow(record?.sub_theme?.theme?.id || '', 'cell')}>
                {record?.sub_theme?.theme?.name}
              </span>
            )}
          />
          <Table.Column<ITopic>
            dataIndex="report_theme"
            render={(_, record) => renderItem(record?.sub_theme?.theme?.id || '')}
            width={200}
            title="Report Themes"
          />
          <Table.Column<ITopic>
            dataIndex="subject"
            title="Subject"
            render={(text, record: any) => (
              <span style={{ cursor: 'pointer' }} onClick={() => handleCopyRow(record?.sub_theme?.theme?.subject?.id || '', 'cell')}>
                {record?.sub_theme?.theme?.subject?.name}
              </span>
            )}
          />

          <Table.Column
            dataIndex="created_at"
            title="Created At"
            render={value => <DateField value={value} format="LLL" />}
            defaultSortOrder={getDefaultSortOrder('created_at', sorter)}
            sorter
          />
          <Table.Column<ITopic>
            title="Actions"
            dataIndex="actions"
            render={(_, record) => (
              <Space>
                <EditButton
                  size="small"
                  type="link"
                  hideText
                  icon={<Icons.EditOutlined />}
                  recordItemId={record.id}
                  hidden={
                    (record?.questions && record?.questions?.length > 0) ||
                    (record?.paper_template_sub_sections && record?.paper_template_sub_sections.length > 0) ||
                    userRole === 'qcreator' ||
                    userRole === 'qapprover' ||
                    userRole === 'qreviewer'
                  }
                  onClick={() => {
                    editShow(record.id)
                  }}
                />
                <DeleteButton
                  hideText
                  size="small"
                  type="link"
                  icon={<Icons.DeleteOutlined />}
                  recordItemId={record.id}
                  hidden={hideDeleteButton(record) || userRole === 'qcreator' || userRole === 'qapprover' || userRole === 'qreviewer'}
                />
                <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => handleCopyRow(record, 'row')} title="Copy Row Data" />
              </Space>
            )}
          />
        </Table>
        <CreateTopic modalProps={modalProps} formProps={formProps} />
        <EditTopic modalProps={editModalProps} formProps={editFormProps} />
        <UploadTopics modalProps={uploadProps} close={closeUpload} reloadUploadedData={reloadUploadedData} />
      </List>
    </>
  )
}
